import { fetchMany } from 'db/fetch'
import { createRecord, updateRecordBatch } from 'db/mutate'
import { query, where, increment } from 'firebase/firestore'
import moment from 'moment'
import { Entry } from 'types/Entry'
import { Stat, StatInterval } from 'types/Stat'
import { entryWordCount } from './entries'

/**
 * Get the stats for the current day, week, month, year and all-time. Creates the stats if they don't exist.
 * @param date
 * @returns The stats for the current day, week, month, year and all-time.
 */
export const getStats = async (date: Date = new Date()): Promise<Stat[]> => {
  const year = moment(date).format('YYYY')
  const month = moment(date).format('YYYY-MM')
  const week = moment(date).format('YYYY-WW')
  const day = moment(date).format('YYYY-MM-DD')

  const ids = [
    'all',
    `yearly-${year}`,
    `monthly-${month}`,
    `weekly-${week}`,
    `daily-${day}`,
  ]

  const currentStats = await fetchMany<Stat>('stats', (q) =>
    query(q, where('id', 'in', ids))
  )

  let created = false

  for (const id of ids) {
    if (!currentStats.find((s) => s.id === id)) {
      // Create the stat
      await createRecord<Stat>(
        'stats',
        {
          period: id.split('-')[1] ?? id,
          interval: id.split('-')[0] as StatInterval,
          entries: 0,
          words: 0,
          goalsCompleted: 0,
        },
        id
      )
      created = true
    }
  }

  if (created) {
    return await getStats(date)
  }

  return currentStats
}

/**
 * Update the entry stats given an entry.
 * @param entry The entry to update the stats with.
 * @param remove Whether to remove the entry stats.
 * @returns
 */
export const updateEntryStats = async (entry: Entry, remove = false) => {
  const currentStats = await getStats(moment(entry.day).toDate())
  if (currentStats.length === 0) {
    return
  }

  const wordCount = entryWordCount(entry)

  await updateRecordBatch<Stat>(
    'stats',
    currentStats.map((s) => ({
      id: s.id!,
      data: {
        words: increment((remove ? -1 : 1) * wordCount),
        entries: increment(remove ? -1 : 1),
      },
    }))
  )
}

/**
 * Update the longest streak for the current year and all-time stats.
 * @param currentStreak The current streak.
 * @returns
 */
export const updateStatsLongestStreak = async (currentStreak: number) => {
  const currentStats = await getStats()
  if (currentStats.length === 0) {
    console.error('Could not find any stats to update streak')
    return
  }

  const statsToUpdate: Stat[] = []

  // Get the current year and all-time stats
  const currentYearStats = currentStats.find(
    (s) => s.interval === 'yearly' && s.period === moment().format('YYYY')
  )
  const allStats = currentStats.find((s) => s.interval === 'all')

  // Add the currentYear stats to the batch update
  // if the current streak is longer than the currentYear streak
  if (
    currentYearStats &&
    currentStreak > (currentYearStats.longestStreak ?? 0)
  ) {
    statsToUpdate.push(currentYearStats)
  }

  // Add the all-time stats to the batch update
  // if the current streak is longer than the all-time streak
  if (allStats && currentStreak > (allStats.longestStreak ?? 0)) {
    statsToUpdate.push(allStats)
  }

  // Batch update the stats
  if (statsToUpdate.length) {
    await updateRecordBatch<Stat>(
      'stats',
      statsToUpdate.map((s) => ({
        id: s.id!,
        data: {
          longestStreak: currentStreak,
        },
      }))
    )
  }
}

/**
 * Update the goals stats given an increment value.
 * @param incrementBy The value to increment by.
 * @returns
 */
export const updateGoalStats = async (incrementBy = 1) => {
  const currentStats = await getStats()
  if (currentStats.length === 0) {
    return
  }

  await updateRecordBatch<Stat>(
    'stats',
    currentStats.map((s) => ({
      id: s.id!,
      data: {
        goalsCompleted: increment(incrementBy),
      },
    }))
  )
}
